import React from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useTranslation, Trans } from 'react-i18next';
import { Form, Icon, Button } from '@weezevent/nacre';
import { fetcher } from '@weezevent/weezjs-core';
import GalleryModal from '../../../components/gallery';
import UploadRequest from '../../../components/gallery/request';
import UploadContainer from '../../../components/gallery/uploadImg';
import LineImage from './lineImage';
import ConfirmModal from '../../../components/confirmModal';
import { UploadDropZone } from '../../../nacre/uploadButton';
import { fileExtensionsToAccept } from '../../../nacre/uploadButton/stateless/mimeTypes';
import '../../../components/upload/uploadFileZone.scss';
import EventBanner from '../gallery/eventBanner';

export const FileContext = React.createContext({ fileType: 'image' });

// Wait for upload and call next
async function wait(upload, next) {
    try {
        const key = await upload;
        next(key);
    } catch (e) {
        next(null);
        throw e;
    }
}

function ModifyAndDeleteButton({ basic, image, fileTypes, onDelete, onDrop, maxSize, setOpenModal }) {
    const { t } = useTranslation();
    const acceptedTypes = React.useMemo(() => {
        return fileExtensionsToAccept(fileTypes);
    }, [fileTypes]);

    const { getInputProps } = useDropzone({
        accept: acceptedTypes,
        onDrop: onDrop,
        multiple: false,
        maxSize: maxSize
    });

    return (
        <div className={'upload-preview-button-panel__container'}>
            <div>
                <input {...getInputProps()} />
                <div className={'dropzone-button'} onClick={() => setOpenModal(true)}>
                    {image ? t('ticket.event.modifyLogo') : t('ticket.event.minisite.add-image')}
                </div>
            </div>
            {image && !basic && <div className={'dropzone-button-separator'}>|</div>}
            {image && basic ? (
                <Icon name="cross" size="small" className={'dropzone-remove-icon'} onClick={onDelete} />
            ) : (
                image && (
                    <div className={'dropzone-button'} onClick={onDelete}>
                        {t('ticket.event.removeLogo')}
                    </div>
                )
            )}
        </div>
    );
}

ModifyAndDeleteButton.propTypes = {
    image: PropTypes.string,
    fileTypes: PropTypes.array,
    onDelete: PropTypes.func,
    onDrop: PropTypes.func,
    maxSize: PropTypes.number,
    basic: PropTypes.bool
};

ModifyAndDeleteButton.defaultProps = {
    basic: false
};

export default function UploadImageZoneGemino({
    organization,
    event,
    inline = false,
    inModal = false,
    modalOnly = false,
    onUpload = () => {},
    onDelete = () => {},
    onCancel = () => {},
    openModal = false,
    mode = 'default',
    image = null,
    textInfo = null,
    fileTypes = UploadImageZoneGemino.fileTypes,
    title = null,
    maxSize = Infinity,
    images = [],
    files = [],
    fileType = 'image',
    args = null
}) {
    const { t } = useTranslation();
    const [fileErrors, setFileErrors] = React.useState([]);
    const [isOpenModal, setOpenModal] = React.useState(openModal);
    const [dropLoading, setDropLoading] = React.useState(false);
    const [showModal, setShowModal] = React.useState(false);
    const [height, setHeight] = React.useState(null);
    const [width, setWidth] = React.useState(null);

    const imageBadRatio = React.useMemo(() => {
        return (width && height && width / height <= 2.5) || width / height >= 3.5;
    }, [width, height]);

    const errors = {
        'file-invalid-type': t('ticket.event.dropzone_error_file_invalid_type'),
        'file-too-large': t('ticket.event.dropzone_error_file_too_large'),
        'file-too-small': t('ticket.event.dropzone_error_file_too_small'),
        'too-many-files': t('ticket.event.dropzone_error_too_many_files')
    };

    React.useEffect(() => setOpenModal(openModal), [openModal]);

    const onSelected = data => {
        setOpenModal(false);
        onUpload(data);
    };

    const onClose = () => {
        setOpenModal(false);
        onCancel();
        setWidth(null);
        setHeight(null);
    };

    const onDrop = React.useCallback(
        (files, rejected) => {
            setWidth(null);
            setHeight(null);
            if (rejected.length) {
                setFileErrors(rejected[0]['errors'].map(({ code }) => code));
            } else {
                setFileErrors([]);
            }
            const [image] = files;

            if (!image) {
                return;
            }

            // Active loader in dropzone
            setDropLoading(true);

            const authorizationURL = `${window.weezConfig.api.apiUrl}/sites/organizations/${organization.id}/${fileType === 'file' ? 'files/' : ''}gallery`;
            const uploadRequest = new UploadRequest(authorizationURL, { fetch: fetcher });

            return wait(uploadRequest.uploadFile(image), ({ key, href }) => {
                function getMeta(url, callback) {
                    let img = new Image();
                    img.src = url;
                    img.onload = function() {
                        callback({ width: `${this.width}`, height: `${this.height}` });
                        setWidth(this.width);
                        setHeight(this.height);
                    };
                }
                if (fileType === 'image') {
                    getMeta(href, data => {
                        onUpload(href, data);
                        setOpenModal(false);
                    });
                    setShowModal(true);
                } else if (fileType === 'file') {
                    onUpload({ url: href });
                    setOpenModal(false);
                }
                setDropLoading(false);
            });
        },
        [onUpload, setFileErrors, setWidth, setHeight]
    );

    const isBanner = args && args.blockType === 'banner';
    const customTabs = React.useMemo(() => {
        if (!event) {
            return [];
        }
        return [
            {
                icon: 'tickets',
                content: t(['ticket.event.minisite.event-banner']),
                children: <EventBanner event={event} onSelected={onSelected} organizationId={organization.id} />
            }
        ];
    }, [event, onSelected, organization]);

    if (modalOnly) {
        return (
            <React.Fragment>
                <ConfirmModal
                    hideCloseButton
                    onAccept={() => setShowModal(false)}
                    objectToSave={showModal && imageBadRatio}
                    labelBody={<Trans i18nKey="ticket.event.minisite.gallery.confirm-warning" />}
                    labelButton={t('ticket.event.minisite.gallery.accept')}
                    labelTitle={t('ticket.event.minisite.gallery.confirm')}
                />
                <FileContext.Provider value={{ fileType: fileType, args: args }}>
                    <GalleryModal
                        apiKeyUnsplash={window.weezConfig.unsplash}
                        isBanner={isBanner}
                        customTabs={customTabs}
                        onSelected={onSelected}
                        organization={organization}
                        event={event}
                        onUpload={onUpload}
                        fileType={fileType}
                        isOpenModal={isOpenModal}
                        onClose={onClose}
                        image={image}
                        maxSize={maxSize}
                    />
                </FileContext.Provider>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <FileContext.Provider value={{ fileType: fileType, args: args }}>
                <GalleryModal
                    apiKeyUnsplash={window.weezConfig.unsplash}
                    isBanner={isBanner}
                    customTabs={customTabs}
                    onSelected={onSelected}
                    organization={organization}
                    event={event}
                    onUpload={onUpload}
                    loading={dropLoading}
                    fileType={fileType}
                    isOpenModal={isOpenModal}
                    onClose={onClose}
                    image={image}
                    maxSize={maxSize}
                />
                {inModal ? (
                    <div className="upload-zone-file-container">
                        {mode === 'carousel' ? (
                            <UploadContainer onClose={onClose} organization={organization} isBanner={isBanner} fileType={fileType} onUpload={onUpload} />
                        ) : (
                            <UploadDropZone
                                onFileSelected={onDrop}
                                acceptedFileExtensions={fileType === 'file' ? [] : fileTypes}
                                icon="upload"
                                maxSize={maxSize}
                                label={null}
                                textInfo={textInfo}
                            />
                        )}
                        <div className="dropzone-or">
                            <span>{t('ticket.common.or')}</span>
                        </div>
                        <Button fluid onClick={() => setOpenModal(true)}>
                            {t(`ticket.event.minisite.add-${fileType}`)}
                        </Button>

                        {fileErrors.map(error => {
                            return (
                                <div className={'dropzone-error-over-limit'} key={error}>
                                    {errors[error]}
                                </div>
                            );
                        })}
                        {images.map((image, i) => (
                            <div key={i} style={{ marginTop: '15px' }}>
                                <LineImage src={image} onDelete={() => onDelete(image, i)} labelButton={t('ticket.common.action.delete')} />
                            </div>
                        ))}
                        {files.map((file, i) => (
                            <div key={i} style={{ marginTop: '15px' }}>
                                <LineImage
                                    src={file.url}
                                    onDelete={() => onDelete(file, i)}
                                    labelButton={t('ticket.common.action.delete')}
                                    onInputChange={name => onUpload({ url: file.url, name: name })}
                                    name={file.name}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="upload-zone-file-container">
                        {title && <Form.Label label={title} optionalText={t('ticket.common.labelOptional')} />}
                        {!images.length && (
                            <UploadDropZone textInfo={textInfo} onFileSelected={onDrop} acceptedFileExtensions={fileTypes} icon="upload" maxSize={maxSize} label={null} />
                        )}
                        {images.length > 0 && (
                            <div style={{ justifyContent: 'center', display: 'flex' }}>
                                <img alt="event logo" className="preview-image-zone" src={images} />
                            </div>
                        )}
                        {fileErrors.map(error => {
                            return (
                                <div className={'dropzone-error-over-limit'} key={error}>
                                    {errors[error]}
                                </div>
                            );
                        })}
                        <div className={inline && 'dropzone-centered'}>
                            <ModifyAndDeleteButton
                                basic={!inline}
                                image={images.length > 0 && images[0]}
                                onDelete={() => onDelete()}
                                onDrop={onDrop}
                                maxSize={maxSize}
                                fileTypes={fileTypes}
                                setOpenModal={setOpenModal}
                            />
                        </div>
                    </div>
                )}
            </FileContext.Provider>
        </React.Fragment>
    );
}

UploadImageZoneGemino.propTypes = {
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    onUpload: PropTypes.func,
    fileTypes: PropTypes.array,
    maxSize: PropTypes.number,
    title: PropTypes.string,
    textInfo: PropTypes.node,
    onDelete: PropTypes.func,
    images: PropTypes.arrayOf(PropTypes.string),
    inline: PropTypes.bool,
    inModal: PropTypes.bool,
    fileType: PropTypes.oneOf(['image', 'file'])
};

UploadImageZoneGemino.fileTypes = ['.jpg', '.jpeg', '.png', '.gif'];
